/**
 * <search-bar> wraps the header's inline predictive search and runs the animated
 * placeholder that teaches shoppers how to search (by product, brand, vehicle, or SKU).
 *
 * Phrases come from the `data-examples` attribute (a JSON array). The first phrase is
 * the steady teaching line and the static fallback; the rest are typed out, held, and
 * cleared in a loop. Animation is paused while the field is focused or has a value, and
 * is skipped entirely when the visitor prefers reduced motion.
 *
 * Each step is scheduled with setTimeout for exactly when the next character is due (a
 * continuous requestAnimationFrame loop burned ~1s of main-thread time in Lighthouse), but
 * the DOM mutation itself runs inside a one-shot requestAnimationFrame - rAF never fires in
 * a hidden tab, so the animation still freezes in the background and resumes on return
 * instead of crawling under the browser's background-timer throttling.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

const TYPE_MS = 70; // per character while typing
const DELETE_MS = 45; // per character while clearing
const HOLD_MS = 1600; // pause on a fully typed phrase
const GAP_MS = 360; // pause between phrases

class SearchBar extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {string[]} */
  #phrases = [];
  /** @type {number | null} setTimeout handle for the next due step */
  #timer = null;
  /** @type {number | null} requestAnimationFrame handle */
  #frame = null;
  #phraseIndex = 0;
  #charIndex = 0;
  #deleting = false;
  #running = false;

  connectedCallback() {
    this.#input = this.querySelector('input.search-input');
    if (!this.#input) return;

    this.#phrases = this.#parsePhrases();

    // The first phrase is the steady label / no-JS / reduced-motion fallback.
    if (this.#phrases.length > 0) {
      this.#input.placeholder = this.#phrases[0];
    }

    // Nothing to animate without at least two phrases, or when motion is reduced.
    if (this.#phrases.length < 2 || REDUCED_MOTION.matches) return;

    this.#input.addEventListener('focus', this.#handleFocus);
    this.#input.addEventListener('blur', this.#handleBlur);
    REDUCED_MOTION.addEventListener?.('change', this.#handleMotionChange);

    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#input?.removeEventListener('focus', this.#handleFocus);
    this.#input?.removeEventListener('blur', this.#handleBlur);
    REDUCED_MOTION.removeEventListener?.('change', this.#handleMotionChange);
  }

  /** @returns {string[]} */
  #parsePhrases() {
    try {
      const raw = this.getAttribute('data-examples');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((phrase) => String(phrase).trim()).filter(Boolean);
    } catch {
      return [];
    }
  }

  #start() {
    if (this.#running || !this.#input) return;
    this.#running = true;

    // Begin by clearing the already-shown label, then type the first example.
    this.#phraseIndex = 0;
    this.#charIndex = this.#phrases[0].length;
    this.#deleting = true;
    this.#schedule(0);
  }

  #stop() {
    this.#running = false;
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
  }

  #handleFocus = () => {
    this.#stop();
    if (this.#input) this.#input.placeholder = this.#phrases[0] ?? '';
  };

  #handleBlur = () => {
    if (!this.#input || this.#input.value.trim().length > 0) return;
    this.#start();
  };

  /** @param {MediaQueryListEvent} event */
  #handleMotionChange = (event) => {
    if (event.matches) {
      this.#stop();
      if (this.#input) this.#input.placeholder = this.#phrases[0] ?? '';
    } else if (!this.#running && document.activeElement !== this.#input) {
      this.#start();
    }
  };

  /**
   * Wait until the next step is due, run it on an animation frame, then schedule the one
   * after. In a hidden tab the pending rAF never fires (and no further timeouts are
   * created), so the animation freezes mid-phrase and picks up when the tab is shown again.
   * @param {number} delay ms until the next step
   */
  #schedule(delay) {
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = null;
        if (!this.#running || !this.#input) return;
        this.#schedule(this.#step());
      });
    }, delay);
  }

  /**
   * Mutate the placeholder by one character and return how long to hold before the next.
   * @returns {number} delay in ms until the next step
   */
  #step() {
    const phrase = this.#phrases[this.#phraseIndex] ?? '';

    if (this.#deleting) {
      this.#charIndex -= 1;
      this.#input.placeholder = phrase.slice(0, Math.max(this.#charIndex, 0));
      if (this.#charIndex <= 0) {
        this.#deleting = false;
        this.#phraseIndex = (this.#phraseIndex + 1) % this.#phrases.length;
        return GAP_MS;
      }
      return DELETE_MS;
    }

    this.#charIndex += 1;
    this.#input.placeholder = phrase.slice(0, this.#charIndex);
    if (this.#charIndex >= phrase.length) {
      this.#deleting = true;
      return HOLD_MS;
    }
    return TYPE_MS;
  }
}

if (!customElements.get('search-bar')) {
  customElements.define('search-bar', SearchBar);
}
